import { useGetAllMySuppliers } from '@/api/supplier.api';
import ArrowLeftIcon from '@/assets/icons/arrow-left.svg?react';
import PlusIcon from '@/assets/icons/plus.svg?react';
import SaveIcon from '@/assets/icons/save-02.svg?react';
import StyledButton from '@/components/styled/StyledButton';
import StyledButtonLoader from '@/components/styled/StyledButtonLoader';
import StyledContainer from '@/components/styled/StyledContainer';
import StyledFormHeaderWithBreadcrumb from '@/components/styled/StyledFormHeaderWithBreadcrumb';
import HorizontalLine from '@/components/ui/HorizontalLine';
import { PATHS } from '@/constants/Paths';
import { buildBenefitsListSearch } from '@/utils/searchParams/buildBenefitsListSearch';
import { getListSearchFromLocation } from '@/utils/searchParams/getListSearchFromLocation';
import { useCreateActivity, useUpdateActivity } from '@/hooks/useActivity';
import { BenefitType } from '@/types/benefits/benefitTypes';
import { SupplierStatus } from '@/types/suppliers/SupplierStatus';
import { zodResolver } from '@hookform/resolvers/zod';
import clsx from 'clsx';
import { ArrowRightIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import {
  FormProvider,
  SubmitHandler,
  useForm,
  useWatch,
} from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router';
import { useToast } from '@/hooks/useToast';
import { useGetBenefitsCities } from '@/hooks/useBenefits';
import { useTranslation } from '@/i18n';
import {
  ActivityFormDataWithTranslations,
  ActivitySchemaWithTranslations,
} from '@/i18n/translation-type-utils';
import { useTranslateFields } from '@/hooks/useTranslate';
import { useResourceLanguage } from '@/hooks/useResourceLanguage';
import LanguageSwitchWithTranslateBtn from '@/components/translation/LanguageSwitchWithTranslateBtn';
import ActivityConfiguration from '@/components/activity/ActivityConfiguration';
import StyledStepper from '@/components/styled/StyledStepper';
import { GENERAL_BENEFITS_STEPS } from '@/constants/steppers/generalBenefitsSteps';
import { useTranslatedConst } from '@/hooks/useTranslatedConst';
import BenefitPricingSummary from '@/components/benefits/BenefitPricingSummary';
import { MarginRateSchema } from '@/types/common/margin-rate.schema';

interface CreateActivityPageProps {
  type?: BenefitType.ACTIVITY | BenefitType.MEAL | BenefitType.TRANSFER;
}

type SubmitFormType = (
  isDraft?: boolean,
) => SubmitHandler<ActivityFormDataWithTranslations>;

const CreateActivityPage = ({
  type = BenefitType.ACTIVITY,
}: CreateActivityPageProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const getTranslatedConst = useTranslatedConst();
  const methods = useForm<ActivityFormDataWithTranslations>({
    resolver: zodResolver(ActivitySchemaWithTranslations),
    mode: 'onChange',
    defaultValues: {
      name: undefined,
      supplier: undefined,
      currency: undefined,
      country: undefined,
      city: undefined,
      marginRate: undefined,
      shortDescription: undefined,
      longDescription: undefined,
      images: [],
      tarifType: 'par-prestation',
      customTarif: 'false',
      childAgeFrom: 1,
      childAgeTo: 12,
      tarifs: [
        {
          name: undefined,
          periods: [
            {
              startDate: undefined,
              endDate: undefined,
              applicationDays: [],
            },
          ],
          pricePartitions: [
            {
              price: undefined,
              from: 1,
              to: 1,
            },
          ],
        },
      ],
    },
  });

  const {
    control,
    handleSubmit,
    formState: { isValid, errors },
    getValues,
    setValue,
  } = methods;

  const currentName = useWatch({ control, name: 'name' });
  const selectedSupplier = useWatch({ control, name: 'supplier' });
  const selectedCountry = useWatch({ control, name: 'country' });

  const [benefitId, setBenefitId] = useState<string | null>(null);

  const { data: cities } = useGetBenefitsCities({
    country: selectedCountry?.value,
  });
  const { mutateAsync: createActivity } = useCreateActivity();
  const { mutateAsync: updateActivity } = useUpdateActivity();
  const { data: suppliers } = useGetAllMySuppliers();

  const errorsLength = useMemo(() => Object.keys(errors).length, [errors]);

  const { language, setLanguage } = useResourceLanguage();
  const { translateFields, isTranslating } = useTranslateFields({
    targetLanguage: language,
    control,
    setValue,
    fields: ['benefit.name', 'shortDescription', 'longDescription'] as const,
  });

  const { toastAsync } = useToast();

  const submitForm: SubmitFormType =
    (isDraft = true) =>
    async (data) => {
      const formData = new FormData();
      const addIf = (value: string | Blob | undefined, key: string) => {
        if (value !== undefined) formData.append(key, value);
      };
      if (!isDraft) formData.append('status', SupplierStatus.PUBLISHED);
      addIf(data.name, 'name');
      addIf(data.supplier?.value, 'supplierId');
      addIf(data.currency?.value, 'currency');
      addIf(data.country?.value, 'country');
      addIf(data.city?.value, 'city');
      const parsedMarginRate = MarginRateSchema.safeParse(data.marginRate);
      if (parsedMarginRate.success && parsedMarginRate.data !== undefined)
        formData.append('marginRate', String(parsedMarginRate.data));
      addIf(data.shortDescription, 'shortDescription');
      addIf(data.longDescription, 'longDescription');
      if (data.tarifType)
        formData.append(
          'pricingType',
          data.tarifType === 'par-prestation' ? 'byBenefit' : 'byPerson',
        );
      addIf(data.customTarif, 'specialTarif');
      if (data.customTarif && data.childAgeFrom)
        formData.append('childAgeFrom', String(data.childAgeFrom));
      if (data.customTarif && data.childAgeTo)
        formData.append('childAgeTo', String(data.childAgeTo));
      if (data.tarifs) formData.append('pricings', JSON.stringify(data.tarifs));
      data.images.forEach((image) => formData.append('files', image));
      formData.append('translations', JSON.stringify(data.translations));
      if (!benefitId) {
        formData.append(
          'activityType',
          type === BenefitType.ACTIVITY
            ? 'ACTIVITY'
            : type === BenefitType.MEAL
              ? 'MEAL'
              : 'TRANSFER',
        );
        const result = await toastAsync(createActivity(formData), {
          success: t('benefits.benefitCreatedSuccess'),
        });
        setBenefitId(result.data.benefitId);
      } else {
        await toastAsync(updateActivity({ benefitId, data: formData }), {
          success: t('benefits.benefitUpdatedSuccess'),
        });
      }
      if (!isDraft)
        navigate(
          PATHS.home.benefits.list +
            getListSearchFromLocation(location, buildBenefitsListSearch),
        );
    };

  const getTitle = () => {
    switch (type) {
      case BenefitType.ACTIVITY:
        return t('activities.newActivity');
      case BenefitType.MEAL:
        return t('activities.newMeal');
      case BenefitType.TRANSFER:
        return t('activities.newTransfer');
      default:
        return t('activities.newActivity');
    }
  };

  const [currStep, setCurrStep] = useState(0);

  return (
    <FormProvider {...methods}>
      <StyledContainer>
        <StyledFormHeaderWithBreadcrumb
          title={currentName || getTitle()}
          breadcrumb={
            <StyledButton
              variant="link"
              title={t('common.backToList')}
              className="text-sm text-[#3D3D3D] pl-0"
              onClick={() =>
                navigate(
                  PATHS.home.benefits.list +
                    getListSearchFromLocation(
                      location,
                      buildBenefitsListSearch,
                    ),
                )
              }
              icon={<ArrowLeftIcon />}
            />
          }
        >
          <StyledButton
            title={t('common.cancel')}
            onClick={() =>
              navigate(
                PATHS.home.benefits.list +
                  getListSearchFromLocation(location, buildBenefitsListSearch),
              )
            }
            variant="outlined"
            className="font-semibold gap-1 px-3 py-2"
          />
          <StyledButtonLoader
            title={t('common.saveAsDraft')}
            successTitle={t('common.draftSaved')}
            onClick={() => submitForm(true)(getValues())}
            disabled={
              currentName == undefined ||
              selectedSupplier == undefined ||
              selectedCountry == undefined
            }
            className="font-semibold gap-1 px-3 py-2"
            icon={<SaveIcon className="w-5 h-5" />}
          />
        </StyledFormHeaderWithBreadcrumb>
        <StyledStepper
          activeStep={currStep}
          steps={getTranslatedConst(GENERAL_BENEFITS_STEPS)}
          setStep={(step: number) => setCurrStep(step)}
        />
        <HorizontalLine />
        <LanguageSwitchWithTranslateBtn
          translateFields={() =>
            translateFields({ translateToAllLocales: true })
          }
          isTranslating={isTranslating}
          language={language}
          setLanguage={setLanguage}
        />
        {currStep === 0 ? (
          <>
            <ActivityConfiguration
              control={control}
              suppliers={suppliers}
              cities={cities}
              language={language}
              isEditEnabled={true}
              translateFields={(fields) =>
                translateFields({ fieldsToTranslate: fields })
              }
              isTranslating={isTranslating}
              errorsLength={errorsLength}
              activityType={type}
            />
            <div className="flex flex-row-reverse p-6">
              <StyledButtonLoader
                title={t('common.saveAsDraft')}
                successTitle={t('common.draftSaved')}
                onClick={() => submitForm(true)(getValues())}
                disabled={
                  currentName == undefined ||
                  selectedSupplier == undefined ||
                  selectedCountry == undefined
                }
                className="font-semibold gap-1 px-3 py-2"
                icon={<SaveIcon className="w-5 h-5" />}
              />
            </div>
            <div className="flex flex-row-reverse p-6">
              <StyledButtonLoader
                title={t('activities.publishActivity')}
                className="font-semibold gap-1 px-3 py-2"
                successTitle={t('common.publishedSuccess')}
                hasSucceeded={!errorsLength}
                errorTitle={t('common.checkRequiredFields')}
                onClick={handleSubmit(submitForm(false))}
                icon={
                  <PlusIcon
                    className={clsx('w-5 h-5', {
                      'fill-white': isValid,
                      'fill-gray-400': !isValid,
                    })}
                  />
                }
              />
            </div>
            <div className="flex flex-row-reverse px-6 pb-6 md:w-fit md:sticky md:left-full md:bottom-0">
              <StyledButton
                title={t('common.pricingSummary')}
                icon={<ArrowRightIcon className="w-5 h-5" />}
                iconPosition="right"
                className="font-semibold gap-1 px-3 py-2"
                onClick={() => {
                  setCurrStep(1);
                  window.scrollTo(0, 0);
                }}
              />
            </div>
          </>
        ) : (
          <>
            <BenefitPricingSummary control={control} />
            <div className="flex flex-row-reverse p-6 md:sticky md:bottom-0 z-10 bg-white">
              <StyledButtonLoader
                title={t('activities.publishActivity')}
                className="font-semibold gap-1 px-3 py-2"
                onClick={handleSubmit(submitForm(false))}
                successTitle={t('common.publishedSuccess')}
                hasSucceeded={!errorsLength}
                errorTitle={t('common.checkRequiredFields')}
                icon={
                  <PlusIcon
                    className={clsx('w-5 h-5', {
                      'fill-white': isValid,
                      'fill-gray-400': !isValid,
                    })}
                  />
                }
                iconPosition="right"
              />
            </div>
          </>
        )}
      </StyledContainer>
    </FormProvider>
  );
};

export default CreateActivityPage;
